import {
  Box, Flex, List, ListItem, VStack, Text,
} from "@chakra-ui/react"
import React, { ReactNode } from "react"
import Link from "components/elements/Link"
import Icon from "components/elements/Icon"
import Button from "../Buttons/Button"

type Props = {
  title: string
  descriptionItems?: (string | ReactNode)[]
  icon?: string
  callsToActions?: {
    text: string,
    icon?: string,
    action?: () => void,
    isButton?: boolean,
    link?: string
  }[]
  horizontalCTAs?: boolean
  footer?: ReactNode
}

type CTA = NonNullable<Props["callsToActions"]>[number]

type CTAItemProps = {
  cta: CTA
  getCtaIconSize: (ctaIcon?: string, isButton?: boolean) => number | undefined
}

const CTAItem = ({ cta, getCtaIconSize }: CTAItemProps) => {
  const {
    icon: ctaIcon, text, action, isButton, link,
  } = cta

  if (isButton) {
    return (
      <Button
        size="lg"
        minH={12}
        px={6}
        fontSize="lg"
        leftIcon={ctaIcon}
        iconSize={4}
        onClick={action}
        {...link ? { href: link, target: "_blank" } : {}}
      >
        {text}
      </Button>
    )
  }

  return (
    <Link
      onClick={action}
      textDecoration="none"
      fontSize="lg"
      href={link}
      isExternal
    >
      <Text as="span">{ctaIcon && <Icon icon={ctaIcon} size={getCtaIconSize(ctaIcon, false)} mr={3} />}{text}</Text>
    </Link>
  )
}

const NoContent = ({
  title,
  descriptionItems = [],
  icon,
  callsToActions = [],
  horizontalCTAs = false,
  footer,
}: Props) => {
  const getCtaIconSize = (ctaIcon?: string, isButton?: boolean) => {
    if (!ctaIcon) return undefined
    if (!isButton) return 6
    return undefined
  }

  const descriptionList = descriptionItems.map((descriptionItem, index) => (
    <ListItem key={index}>{descriptionItem}</ListItem>
  ))

  const callToActionList = callsToActions.map((cta, index) => (
    <CTAItem key={index} cta={cta} getCtaIconSize={getCtaIconSize} />
  ))

  const buttonCallToActionList = callsToActions
    .filter(({ isButton }) => isButton)
    .map((cta, index) => (
      <CTAItem key={`button-${index}`} cta={cta} getCtaIconSize={getCtaIconSize} />
    ))

  const nonButtonCallToActionList = callsToActions
    .filter(({ isButton }) => !isButton)
    .map((cta, index) => (
      <CTAItem key={`non-button-${index}`} cta={cta} getCtaIconSize={getCtaIconSize} />
    ))

  return (
    <Flex
      direction="column"
      justify="center"
      minHeight="full"
      textAlign="center"
      align="center"
      gap={6}
    >
      <Box w={1 / 3}>
        {icon && <Icon icon={icon} size={20} />}
      </Box>
      <Text fontSize="2xl" fontWeight="semibold" lineHeight="1.2">{title}</Text>
      <List fontSize="lg" maxWidth={600} display="flex" flexDirection="column" gap={4}>
        {descriptionList}
      </List>
      {horizontalCTAs ? (
        <Flex direction="column" align="center" gap={6}>
          {!!buttonCallToActionList.length && (
            <Flex gap={4} wrap="wrap" justify="center">
              {buttonCallToActionList}
            </Flex>
          )}
          {!!nonButtonCallToActionList.length && (
            <Flex direction="column" align="center" gap={6}>
              {nonButtonCallToActionList}
            </Flex>
          )}
        </Flex>
      ) : (
        <VStack spacing={4}>
          {callToActionList}
        </VStack>
      )}
      {footer && footer}
    </Flex>
  )
}

export default NoContent
